import { useQueries } from '@tanstack/react-query';
import axiosClient, { remoteEnvBaseUrl } from './axiosClient';
import { useAppEnv } from './useAppEnv';
import { useTenantEditability } from './useTenantEditability';
import { setTenantHome } from './runtimeTenantHome';
import { parseSemver, compareSemver } from '../utils/semver';
import { HAS_UNPUBLISHED_CHANGES_KEY } from './useHasUnpublishedChanges';
import { useVisibleLadderEnvs } from '../context/useVisibleLadderEnvs';

export interface TenantVersionRow {
  env: string;
  isHome: boolean;
  versionStr: string | null;
  promoted: boolean;
  isPartial?: boolean;
  advancedAt?: string;
  advancedBy?: string;
  isLoading: boolean;
  isError: boolean;
  behind: number;
  isLatest: boolean;
}

interface TenantVersionResp {
  env: string;
  tenantCode: string;
  versionStr: string | null;
  promoted: boolean;
  isPartial?: boolean;
  advancedAt?: string;
  advancedBy?: string;
}

/**
 * Phase 4 — one tenant's version pointer across every readable env (parallel,
 * per-env tolerant). Home: bare /api/promotion-version/tenants/:code on this
 * backend. Remotes: browser-direct to https://dosa.<env>.../api/promotion-version/
 * tenants/:code (the home pod can't reach sibling envs over the mesh; the browser
 * can, and each env's CORS + shared Auth0 audience make the direct call work).
 * All GET → read-only safe.
 */
export function useTenantVersions(tenantCode: string | undefined): {
  rows: TenantVersionRow[];
  isLoading: boolean;
} {
  const appEnv = useAppEnv();
  // HOME = the tenant's ACTUAL authoring env (config.tenant_editing_env), NOT
  // appEnv.editableEnv (which is the legacy build-time 'dev01' — using it made a
  // qa-homed tenant show dev01 as HOME, hide the qa row entirely, and fan the
  // version probes at the wrong ladder). Fall back to editableEnv only when the
  // per-tenant backend is absent (flag-off / 404 degrade) or no tenant is set.
  const editability = useTenantEditability(tenantCode);
  const fallbackHome = appEnv?.editableEnv ?? 'dev01';
  const home = tenantCode && !editability.isDefaultHome ? editability.homeEnv : fallbackHome;
  // Publish the resolved per-tenant home into the router map so home-authoritative
  // reads (the workflow list) route browser-direct to dosa.<home>. Done HERE (not
  // only in useTenantEditability) because this `home` is deterministic even when
  // the editability query degrades/fails — that's the case where the badge showed
  // dev01 but the map stayed empty, so the workflow read fell back to the portal.
  // Idempotent Map.set, safe during render, and populated on the same page that
  // renders the version cards.
  if (tenantCode && home) setTenantHome(tenantCode, home);
  // Ladder = the tenant's home first, then every other readable env (its promote
  // targets). Home leads so isHome + the local-backend fetch line up on it.
  const rawOrder = appEnv
    ? [home, ...(appEnv.remoteReadableEnvs ?? []).filter((e) => e !== home)]
    : [];
  // Prod-option gating (decisions §8): drop 'prod' from the fan-out when
  // gated — removes the prod row from every consumer of this hook (TopBar
  // version chip, DeployMatrix env columns, PublishTab version cards).
  const order = useVisibleLadderEnvs(rawOrder);

  const results = useQueries({
    queries: order.map((env) => ({
      queryKey: ['tenant-version', env, tenantCode],
      enabled: !!appEnv && !!tenantCode,
      queryFn: async (): Promise<TenantVersionResp> => {
        const path = `/api/promotion-version/tenants/${encodeURIComponent(tenantCode as string)}`;
        // Fetch LOCALLY only for the env THIS pod serves (appEnv.ladderEnv);
        // every other env is browser-direct to its public host (the pod can't
        // reach siblings over the mesh). This is independent of which env is the
        // tenant's HOME — a dev01 portal viewing a qa-homed tenant must still hit
        // qa's public host for qa's pointer, not this dev01 backend. __crossEnv
        // tags remote calls so a remote 401 doesn't tear down the home session.
        const isLocal = env === (appEnv?.ladderEnv ?? home);
        const url = isLocal ? path : `${remoteEnvBaseUrl(env)}${path}`;
        // __envProbe pins this leg to THIS env's own pointer. Without it the LOCAL
        // leg (a relative /api/promotion-version URL) is home-authoritative and
        // crossEnvRequestRouter block (A) rewrites it to dosa.<tenant-home> — so a
        // dev01-homed tenant viewed from dosa.qa showed dev01's version in the qa
        // column (and every column you view from). The matrix needs each env's OWN
        // pointer, so opt out of the home fold-back on every leg.
        const { data } = isLocal
          ? await axiosClient.get(url, { __envProbe: env } as never)
          : await axiosClient.get(url, { __crossEnv: env, __envProbe: env } as never);
        return data as TenantVersionResp;
      },
      staleTime: 15_000,
      // Poll like useEnvVersions does: the promote surfaces pair these pointer
      // rows with workflow queries that ALL poll (pipeline, deploy matrix,
      // workflow detail — the "Refreshing in Ns" ticker). Without a matching
      // poll here, a worker-side apply advances the env pointer but the TENANT
      // VERSION strip, the matrix's current/superseded cells, and the ladder
      // "Locked" gate (ladderLockedBy reads these rows) keep the old world
      // until a manual page refresh — showing "applying…" beside a stale
      // "not promoted"/old version.
      refetchInterval: 30_000,
      retry: 1,
    })),
  });

  const heads = results.map((r) => r.data?.versionStr).filter((v): v is string => !!v);
  const distinct = Array.from(
    new Set(
      heads.map((v) => {
        const t = parseSemver(v);
        return `${t.major}.${t.minor}.${t.patch}`;
      }),
    ),
  )
    .map(parseSemver)
    .sort((a, b) => compareSemver(b, a));

  const rows: TenantVersionRow[] = order.map((env, i) => {
    const r = results[i];
    const d = r.data;
    const v = d?.versionStr ? parseSemver(d.versionStr) : null;
    const myRank = v ? distinct.findIndex((x) => compareSemver(x, v) === 0) : -1;
    return {
      env,
      isHome: env === home,
      versionStr: d?.versionStr ?? null,
      promoted: d?.promoted ?? false,
      isPartial: d?.isPartial,
      advancedAt: d?.advancedAt,
      advancedBy: d?.advancedBy,
      isLoading: r.isLoading,
      isError: r.isError,
      behind: myRank > 0 ? myRank : 0,
      isLatest: myRank === 0,
    };
  });
  return { rows, isLoading: results.some((r) => r.isLoading) };
}

/**
 * Invalidate every version-related query so the UI reflects a version change
 * IMMEDIATELY — a publish mints the tenant's next version and advances the
 * editable-env pointer; a deploy advances a target env's pointer. Without
 * this, the TopBar chip / EnvVersionStrip / version cards keep the stale
 * pointer until the next 30s poll tick (immediate feedback beats waiting a
 * poll cycle for a change THIS browser just made). Prefix keys invalidate all
 * per-env variants at once.
 */
export function invalidateVersionQueries(
  queryClient: { invalidateQueries: (f: { queryKey: readonly unknown[] }) => Promise<unknown> },
  tenantCode?: string,
): Promise<unknown> {
  return Promise.all([
    // Per-env pointer rows (TopBar chip via useTenantVersions, EnvVersionStrip).
    queryClient.invalidateQueries({ queryKey: ['tenant-version'] }),
    // TenantSettings PROMOTION VERSION card.
    queryClient.invalidateQueries({ queryKey: tenantCode ? ['tenant-versions', tenantCode] : ['tenant-versions'] }),
    // Selected-env chip variant (useSelectedEnvVersion).
    queryClient.invalidateQueries({ queryKey: ['selected-env-version'] }),
    // Minted-version catalog (Create Deployment Package picker + DeployMatrix rows).
    queryClient.invalidateQueries({ queryKey: tenantCode ? ['tenant-minted-versions', tenantCode] : ['tenant-minted-versions'] }),
    // Unpublished-changes flag (TopBar pending chip via useHasUnpublishedChanges).
    queryClient.invalidateQueries({ queryKey: tenantCode ? [HAS_UNPUBLISHED_CHANGES_KEY, tenantCode] : [HAS_UNPUBLISHED_CHANGES_KEY] }),
  ]);
}
